import { useState, useEffect } from 'react'
import { Clock, Globe } from 'lucide-react'
import Card from '../components/Card'

const timezones = [
  { name: 'UTC', offset: 0, label: 'UTC (Coordinated Universal Time)' },
  { name: 'America/New_York', offset: -5, label: 'New York (EST)' },
  { name: 'America/Chicago', offset: -6, label: 'Chicago (CST)' },
  { name: 'America/Denver', offset: -7, label: 'Denver (MST)' },
  { name: 'America/Los_Angeles', offset: -8, label: 'Los Angeles (PST)' },
  { name: 'Europe/London', offset: 0, label: 'London (GMT)' },
  { name: 'Europe/Paris', offset: 1, label: 'Paris (CET)' },
  { name: 'Europe/Berlin', offset: 1, label: 'Berlin (CET)' },
  { name: 'Asia/Tokyo', offset: 9, label: 'Tokyo (JST)' },
  { name: 'Asia/Shanghai', offset: 8, label: 'Shanghai (CST)' },
  { name: 'Asia/Dubai', offset: 4, label: 'Dubai (GST)' },
  { name: 'Australia/Sydney', offset: 10, label: 'Sydney (AEST)' },
]

export default function WorldClock() {
  const [selectedTimezones, setSelectedTimezones] = useState(['UTC', 'America/New_York', 'Europe/London', 'Asia/Tokyo'])
  const [currentTimes, setCurrentTimes] = useState({})

  useEffect(() => {
    const updateTimes = () => {
      const times = {}
      selectedTimezones.forEach(tz => {
        try {
          const date = new Date()
          const utcTime = date.getTime() + (date.getTimezoneOffset() * 60000)
          const tzData = timezones.find(t => t.name === tz)
          if (tzData) {
            const localTime = new Date(utcTime + (tzData.offset * 3600000))
            times[tz] = {
              time: localTime.toLocaleTimeString('en-US', { hour12: false }),
              date: localTime.toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })
            }
          }
        } catch (e) {
          times[tz] = { time: 'Error', date: '' }
        }
      })
      setCurrentTimes(times)
    }

    updateTimes()
    const interval = setInterval(updateTimes, 1000)
    return () => clearInterval(interval)
  }, [selectedTimezones])

  const toggleTimezone = (tz) => {
    if (selectedTimezones.includes(tz)) {
      setSelectedTimezones(selectedTimezones.filter(t => t !== tz))
    } else {
      setSelectedTimezones([...selectedTimezones, tz])
    }
  }

  return (
    <div className="space-y-6">
      <Card>
        <Card.Body>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Timezones:</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {timezones.map(tz => (
              <label key={tz.name} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedTimezones.includes(tz.name)}
                  onChange={() => toggleTimezone(tz.name)}
                  className="w-4 h-4"
                />
                <span className="text-sm text-gray-700">{tz.label}</span>
              </label>
            ))}
          </div>
        </Card.Body>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {selectedTimezones.map(tz => {
          const tzData = timezones.find(t => t.name === tz)
          const timeData = currentTimes[tz] || { time: 'Loading...', date: '' }
          
          return (
            <Card key={tz}>
              <Card.Body>
                <div className="flex items-center gap-3 mb-4">
                  <Globe className="w-5 h-5 text-orange-600" />
                  <h3 className="font-semibold text-gray-900">{tzData?.label || tz}</h3>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-orange-600 mb-2">{timeData.time}</div>
                  <div className="text-sm text-gray-600">{timeData.date}</div>
                </div>
              </Card.Body>
            </Card>
          )
        })}
      </div>

      {selectedTimezones.length === 0 && (
        <Card>
          <Card.Body className="text-center py-12">
            <Clock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Select timezones above to view their current times</p>
          </Card.Body>
        </Card>
      )}
    </div>
  )
}

